import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  Upload, 
  Clock, 
  FileText,
  Brain,
  ClipboardList,
  Sparkles,
  LogOut,
  Crown,
  Loader2,
  AlertCircle,
  CheckCircle,
  File,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';
import { API_URL } from '../config/api';

// Upload progress animation component
const UploadProgress = ({ fileName, progress, stage }) => {
  const stages = [
    { label: 'Uploading file...', icon: Upload },
    { label: 'Extracting text...', icon: FileText },
    { label: 'Analyzing content...', icon: Brain },
    { label: 'Ready to study!', icon: CheckCircle }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-6"
    >
      <div className="flex items-center gap-4 mb-6">
        <motion.div
          className="w-16 h-16 bg-primary/10 flex items-center justify-center"
          animate={{ rotate: stage < 3 ? 360 : 0 }}
          transition={{ duration: 2, repeat: stage < 3 ? Infinity : 0, ease: "linear" }}
        >
          <File className="w-8 h-8 text-primary" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{fileName}</p>
          <p className="text-sm text-muted-foreground">
            {stages[stage]?.label || 'Processing...'}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative mb-6">
        <div className="h-2 bg-secondary overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <motion.span
          className="absolute right-0 -top-6 text-sm font-mono text-primary"
          key={progress}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {progress}%
        </motion.span>
      </div>

      {/* Stage indicators */}
      <div className="grid grid-cols-4 gap-2">
        {stages.map((s, i) => (
          <motion.div
            key={i}
            className={`flex flex-col items-center text-center p-2 ${
              i <= stage ? 'text-primary' : 'text-muted-foreground'
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <motion.div
              className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                i < stage ? 'bg-primary text-white' : 
                i === stage ? 'bg-primary/20 text-primary' : 'bg-secondary'
              }`}
              animate={i === stage ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.5, repeat: i === stage ? Infinity : 0 }}
            >
              <s.icon className="w-4 h-4" />
            </motion.div>
            <span className="text-xs">{s.label.split('...')[0]}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default function Dashboard() {
  const { user, accessToken, profile, signout, refreshProfile, refreshSession, canGenerate, remainingGenerations, dailyLimit, isSubscribed } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else if (response.status === 403) {
        // Token might be expired - try to refresh
        console.log('Sessions fetch returned 403 - attempting token refresh');
        const newToken = await refreshSession();
        if (newToken) {
          // Retry with new token
          const retryResponse = await fetch(`${API_URL}/api/sessions`, {
            headers: { 'Authorization': `Bearer ${newToken}` }
          });
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setSessions(data.sessions || []);
          } else {
            toast.error('Session expired. Please log in again.');
          }
        } else {
          toast.error('Session expired. Please log in again.');
        }
      } else {
        console.error('Sessions fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load your study sessions. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, refreshSession]);

  useEffect(() => {
    if (accessToken) {
      fetchSessions();
    }
  }, [accessToken, fetchSessions]);

  const simulateProgress = () => {
    // Simulate upload progress with stages
    let progress = 0;
    let stage = 0;
    
    const interval = setInterval(() => {
      if (progress < 30) {
        progress += Math.random() * 10;
        stage = 0;
      } else if (progress < 60) {
        progress += Math.random() * 8;
        stage = 1;
      } else if (progress < 90) {
        progress += Math.random() * 5;
        stage = 2;
      } else {
        progress = Math.min(progress + 2, 95);
        stage = 2;
      }
      
      setUploadProgress(Math.min(Math.round(progress), 95));
      setUploadStage(stage);
    }, 300);

    return interval;
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed. Please select a .pdf file.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large. Maximum size is 10MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`);
      return;
    }

    if (file.size === 0) {
      toast.error('The selected file is empty. Please choose a valid PDF.');
      return;
    }

    setIsUploading(true);
    setUploadFileName(file.name);
    setUploadProgress(0);
    setUploadStage(0);

    const progressInterval = simulateProgress();
    const formData = new FormData();
    formData.append('file', file);

    try {
      let response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formData
      });

      // If 403, try to refresh token and retry
      if (response.status === 403) {
        console.log('Upload returned 403 - attempting token refresh');
        const newToken = await refreshSession();
        if (newToken) {
          // Recreate form data for retry
          const retryFormData = new FormData();
          retryFormData.append('file', file);
          response = await fetch(`${API_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${newToken}` },
            body: retryFormData
          });
        }
        if (response.status === 403) {
          clearInterval(progressInterval);
          toast.error('Session expired. Please log in again.');
          setIsUploading(false);
          setUploadProgress(0);
          setUploadStage(0);
          return;
        }
      }

      clearInterval(progressInterval);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      // Complete the progress animation
      setUploadProgress(100);
      setUploadStage(3);
      
      // Show warning if there was one
      if (data.warning) {
        toast.warning(data.warning);
      }
      
      // Wait a moment to show completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      analytics.pdfUpload(file.name);
      toast.success('PDF uploaded successfully!');
      navigate(`/study/${data.session_id}`);
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error.message || 'Failed to upload file. Please try again.';
      toast.error(errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStage(0);
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileUpload(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    handleFileUpload(file);
  };

  const cancelUpload = () => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadStage(0);
    setUploadFileName('');
  };

  const handleLogout = async () => {
    await signout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b-2 border-border sticky top-0 bg-white z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
          <div className="flex items-center gap-4">
            {!profile?.is_subscribed && (
              <Link to="/subscription">
                <Button 
                  variant="outline" 
                  className="h-10 px-4 border-2 font-medium hidden sm:flex"
                  data-testid="upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade
                </Button>
              </Link>
            )}
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="h-10 px-4"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            DASHBOARD
          </span>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-1">
            WELCOME BACK
          </h1>
          <p className="text-muted-foreground mt-2">
            {user?.email}
          </p>
        </motion.div>

        {/* Usage Banner */}
        {!isSubscribed && (
          <motion.div 
            className="mb-8 border-2 border-border p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3">
              <motion.div 
                className={`w-10 h-10 flex items-center justify-center ${canGenerate ? 'bg-primary' : 'bg-destructive'}`}
                animate={!canGenerate ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 0.5, repeat: !canGenerate ? Infinity : 0 }}
              >
                {canGenerate ? (
                  <Sparkles className="w-5 h-5 text-white" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-white" />
                )}
              </motion.div>
              <div>
                <p className="font-medium" data-testid="generation-counter">
                  {canGenerate 
                    ? `Free generations remaining today: ${remainingGenerations} / ${dailyLimit}`
                    : "You've reached today's free limit"
                  }
                </p>
                <p className="text-sm text-muted-foreground">
                  {canGenerate 
                    ? 'Resets daily • Upgrade for unlimited'
                    : 'Upgrade to Pro for unlimited generations or come back tomorrow'
                  }
                </p>
              </div>
            </div>
            <Link to="/subscription">
              <Button 
                className="h-10 px-6 font-bold uppercase text-sm"
                data-testid="upgrade-banner-btn"
              >
                Upgrade to Pro
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Upload Section */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="font-heading font-bold text-lg mb-4">UPLOAD MATERIAL</h2>
          
          <AnimatePresence mode="wait">
            {isUploading ? (
              <motion.div
                key="uploading"
                className="border-2 border-primary bg-primary/5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="flex justify-end p-2 border-b border-primary/20">
                  <button
                    onClick={cancelUpload}
                    className="p-1 hover:bg-primary/10 rounded"
                    disabled={uploadStage === 3}
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
                <UploadProgress 
                  fileName={uploadFileName}
                  progress={uploadProgress}
                  stage={uploadStage}
                />
              </motion.div>
            ) : (
              <motion.label 
                key="dropzone"
                className={`border-2 border-dashed p-8 md:p-12 flex flex-col items-center justify-center cursor-pointer transition-all ${
                  dragActive 
                    ? 'border-primary bg-primary/10 scale-[1.02]' 
                    : 'border-border hover:border-primary hover:bg-primary/5'
                }`}
                data-testid="upload-dropzone"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <input 
                  type="file" 
                  accept=".pdf" 
                  className="hidden" 
                  onChange={handleInputChange}
                  data-testid="file-input"
                />
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Upload className={`w-12 h-12 mb-4 ${dragActive ? 'text-primary' : 'text-primary/60'}`} />
                </motion.div>
                <p className="font-medium text-center">
                  {dragActive ? 'Drop your PDF here!' : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  PDF files only (lecture slides, homework, notes)
                </p>
                {dragActive && (
                  <motion.div
                    className="mt-4 px-4 py-2 bg-primary text-white text-sm font-medium"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    Release to upload
                  </motion.div>
                )}
              </motion.label>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Recent Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-heading font-bold text-lg mb-4">RECENT STUDY SESSIONS</h2>
          {isLoading ? (
            <div className="border-2 border-border p-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <motion.div 
              className="border-2 border-border p-8 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No study sessions yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Upload a PDF to get started
              </p>
            </motion.div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -3 }}
                >
                  <Link 
                    to={`/study/${session.id}`}
                    className="block border-2 border-border p-4 hover:border-primary transition-colors"
                    data-testid={`session-card-${session.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-secondary flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{session.file_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(session.created_at)}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
        {/* Footer */}
        <footer className="border-t-2 border-border py-8 px-6 bg-white">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-sm">CALC AI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © Powered by FHYAS LLC.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
